"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { FieldRenderer } from "./shared/field-renderer"
import { getFieldType } from "./shared/schema-utils"

interface ListPreviewProps {
	item: any
	data?: any
	parentData?: any
	schema?: any
	editable?: boolean
	onDataChange?: (path: string, value: any) => void
}

export function ListPreview({ item, data, parentData, schema, editable = false, onDataChange }: ListPreviewProps) {

	const listData = (() => {
		const sourceData = data || parentData

		if (!sourceData) {
			return []
		}

		// Try direct key access first
		if (item.key) {
			const result = sourceData[item.key]

			if (result !== undefined) {
				const arrayResult = Array.isArray(result) ? result : []
				return arrayResult
			}
		}

		// Fall back to scope-based navigation for JSON Schema paths
		if (item.scope) {
			let current = sourceData
			const parts = item.scope.split("/").filter((p) => p && p !== "#" && p !== "properties")

			for (const part of parts) {

				if (current && current[part] !== undefined) {
					current = current[part]
				} else {
					return []
				}
			}

			const scopeResult = Array.isArray(current) ? current : []
			return scopeResult
		}

		return []
	})()

	const listKeys = item.keys && item.keys.length > 0 ? item.keys : []

	const getSchemaForKey = (key: string) => {
		if (!schema || !item.scope) return null
		const parts = item.scope.split("/").filter((p) => p && p !== "#" && p !== "properties")
		let schemaLevel = schema
		for (const part of parts) {
			if (schemaLevel?.properties?.[part]) {
				schemaLevel = schemaLevel.properties[part]
			} else if (schemaLevel?.items?.properties) {
				schemaLevel = schemaLevel.items.properties
			} else {
				return null
			}
		}
		return schemaLevel?.items?.properties?.[key] || schemaLevel?.properties?.[key]
	}

	const handleFieldChange = (listIndex: number, key: string, value: any) => {
		if (!onDataChange) return

		const dataKey =
			item.key ||
			(item.scope
				? item.scope
					.split("/")
					.filter((p) => p && p !== "#" && p !== "properties")
					.join(".")
				: "")
		if (!dataKey) return

		const path = `${dataKey}[${listIndex}].${key}`
		onDataChange(path, value)
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>{item.header || "List"}</CardTitle>
			</CardHeader>
			<CardContent>
				{listData.length > 0 ? (
					listKeys.length > 0 ? (
						editable ? (
							<div className="space-y-4">
								{listData.map((listItem: any, idx: number) => (
									<Card key={idx}>
										<CardHeader>
											<CardTitle className="text-sm">Item {idx + 1}</CardTitle>
										</CardHeader>
										<CardContent className="space-y-3">
											{listKeys.map((key: string) => {
												const value = listItem[key]
												const displayValue = value !== undefined && value !== null ? String(value) : ""
												const fieldSchema = getSchemaForKey(key)
												const fieldType = getFieldType(key, fieldSchema)

												return (
													<FieldRenderer
														key={key}
														fieldKey={key}
														fieldType={fieldType}
														value={displayValue}
														editable={editable}
														onChange={(newValue) => handleFieldChange(idx, key, newValue)}
													/>
												)
											})}
										</CardContent>
									</Card>
								))}
							</div>
						) : (
							<ul className="list-disc list-inside space-y-2">
								{listData.map((listItem: any, idx: number) => (
									<li key={idx} className="text-sm">
										{listKeys.map((key: string, keyIdx: number) => {
											const value = listItem[key]
											const displayValue = value !== undefined && value !== null ? String(value) : "-"
											return (
												<span key={keyIdx}>
													{keyIdx > 0 && " • "}
													<span className="font-medium">
														{key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}:
													</span>{" "}
													{displayValue}
												</span>
											)
										})}
									</li>
								))}
							</ul>
						)
					) : (
						<ul className="list-disc list-inside space-y-2">
							{listData.map((listItem: any, idx: number) => (
								<li key={idx} className="text-sm">
									{typeof listItem === "object" ? JSON.stringify(listItem) : String(listItem)}
								</li>
							))}
						</ul>
					)
				) : (
					<p className="text-sm text-muted-foreground">No items to display</p>
				)}
			</CardContent>
		</Card>
	)
}

